import { useState } from "react";
import { useToast } from "../ToastProvider.jsx";
import { useFocusTrap } from "../../../hooks/useFocusTrap.js";
import { useMemberStore } from "../../../stores/memberStore";
import { useModalStore } from "../../../stores/modalStore";
import { useServerStore } from "../../../stores/serverStore";
import { useAuditStore } from "../../../stores/auditStore.js";

const TIMEOUT_DURATIONS = [
  { label: '60s (test)',  ms: 60000 },
  { label: '5 minutes',  ms: 300000 },
  { label: '10 minutes', ms: 600000 },
  { label: '30 minutes', ms: 1800000 },
  { label: '1 hour',     ms: 3600000 },
  { label: '1 day',      ms: 86400000 },
  { label: '1 week',     ms: 604800000 },
];

async function writeServerAudit(serverId, entry) {
  if (!serverId || serverId.startsWith('demo-')) return;
  try {
    await fetch(`/api/servers/${serverId}/audit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      body: JSON.stringify(entry),
    });
  } catch (_) {}
}

const ROLE_OPTIONS = ['GUEST', 'MEMBER', 'MODERATOR', 'ADMIN'];
const ROLE_META = {
  OWNER:     { icon: '👑', color: '#f59e0b' },
  ADMIN:     { icon: '⚡', color: '#f43f5e' },
  MODERATOR: { icon: '🛡️', color: '#10b981' },
  MEMBER:    { icon: null,  color: 'rgba(255,255,255,0.5)' },
  GUEST:     { icon: null,  color: 'rgba(255,255,255,0.3)' },
};

export function ManageMembersModal() {
  const isOpen  = useModalStore(s => s.isOpen && s.type === 'manageMembers');
  const onClose = useModalStore(s => s.onClose);
  const onOpen  = useModalStore(s => s.onOpen);
  const members          = useMemberStore(s => s.members);
  const updateMemberRole = useMemberStore(s => s.updateMemberRole);
  const kickMember       = useMemberStore(s => s.kickMember);
  const banMember        = useMemberStore(s => s.banMember);
  const timeoutMember    = useMemberStore(s => s.timeoutMember);
  const getCurrentUser   = useMemberStore(s => s.getCurrentUser);
  const currentServerId  = useServerStore(s => s.currentServerId);
  const addAuditEntry    = useAuditStore(s => s.addEntry);
  const { addToast }     = useToast();

  const currentUser = getCurrentUser();
  const isAdmin = currentUser?.role?.toUpperCase() === 'ADMIN' || currentUser?.role?.toUpperCase() === 'OWNER';

  // { memberId, action: 'kick'|'ban'|'timeout' }
  const [confirm, setConfirm] = useState(null);
  const [timeoutDuration, setTimeoutDuration] = useState(TIMEOUT_DURATIONS[2]); // 10 min default
  const [actionLoading, setActionLoading] = useState(false);
  const [search, setSearch] = useState('');
  const trapRef = useFocusTrap(isOpen);

  if (!isOpen) return null;

  const filtered = members.filter(m =>
    (m.name || m.username || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleRoleChange = async (memberId, newRole) => {
    if (!isAdmin) return;
    try {
      if (currentServerId && !currentServerId.startsWith('demo-')) {
        await fetch(`/api/servers/${currentServerId}/members/${memberId}/role`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
          body: JSON.stringify({ role: newRole }),
        });
      }
    } catch (_) {}
    updateMemberRole(memberId, newRole);
  };

  const executeAction = async () => {
    if (!confirm) return;
    setActionLoading(true);
    const { memberId, action } = confirm;
    const target = members.find(m => m.id === memberId);
    const targetName = target?.name || target?.username;

    if (action === 'timeout') {
      timeoutMember(memberId, timeoutDuration.ms);
      addAuditEntry('member_timeout', currentUser?.name || 'Admin', targetName, `Timed out for ${timeoutDuration.label}`);
      addToast({ type: 'success', title: `${targetName} timed out`, message: `Duration: ${timeoutDuration.label}` });
    } else {
      try {
        if (currentServerId && !currentServerId.startsWith('demo-')) {
          await fetch(`/api/servers/${currentServerId}/members/${memberId}/${action}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
          });
          await writeServerAudit(currentServerId, {
            action: action === 'kick' ? 'member_kick' : 'member_ban',
            targetId: memberId,
            targetName,
          });
        }
      } catch (_) {}
      addAuditEntry(action === 'kick' ? 'member_kick' : 'member_ban', currentUser?.name || 'Admin', targetName, null);
      if (action === 'kick') {
        kickMember(memberId);
        addToast({ type: 'warning', title: `${targetName} was kicked` });
      } else {
        banMember(memberId);
        addToast({ type: 'error', title: `${targetName} was banned` });
      }
    }
    setConfirm(null);
    setActionLoading(false);
  };

  const confirmMeta = confirm ? members.find(m => m.id === confirm.memberId) : null;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}
    >
      <div ref={trapRef} role="dialog" aria-modal="true" aria-label="Manage Members" style={{ background: 'rgb(var(--aethex-panel))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, width: 440, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'rgb(var(--aethex-text))' }}>Manage Members</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{members.length} members</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => { onClose(); setTimeout(() => onOpen('auditLog'), 50); }}
              title="View Audit Log"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: 13, padding: '4px 10px', borderRadius: 6, lineHeight: 1 }}>
              📋 Audit Log
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: 18, padding: 4, borderRadius: 6, lineHeight: 1 }}>✕</button>
          </div>
        </div>

        {/* Search */}
        <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexShrink: 0 }}>
          <input
            placeholder="Search members…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: '100%', background: 'rgb(var(--aethex-bg))', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '7px 12px', color: 'rgb(var(--aethex-text))', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>

        {/* Members list */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {filtered.map(member => {
            const role    = (member.role || 'MEMBER').toUpperCase();
            const meta    = ROLE_META[role] || ROLE_META.MEMBER;
            const isOwner = role === 'OWNER';
            const isSelf  = member.id === currentUser?.id;
            const isBanned = member.status === 'banned';

            return (
              <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                {/* Avatar */}
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'linear-gradient(135deg, rgb(var(--aethex-primary)/0.6), rgba(255,255,255,0.1))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0, opacity: isBanned ? 0.4 : 1 }}>
                  {member.avatar || (member.name || '?')[0].toUpperCase()}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: isBanned ? 'rgba(255,255,255,0.3)' : 'rgb(var(--aethex-text))', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {member.name || member.username}
                    </span>
                    {meta.icon && <span style={{ fontSize: 11 }}>{meta.icon}</span>}
                    {isBanned && <span style={{ fontSize: 10, color: '#ef4444', background: 'rgba(239,68,68,0.15)', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>BANNED</span>}
                    {member.timedOutUntil > Date.now() && <span style={{ fontSize: 10, color: '#f59e0b', background: 'rgba(245,158,11,0.15)', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>⏱️ TIMED OUT</span>}
                    {isSelf && <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.06)', padding: '1px 6px', borderRadius: 4 }}>You</span>}
                  </div>
                  <div style={{ fontSize: 11, color: meta.color, marginTop: 1 }}>{role}</div>
                </div>

                {/* Actions */}
                {isAdmin && !isSelf && !isOwner && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <select
                      value={role}
                      onChange={e => handleRoleChange(member.id, e.target.value)}
                      style={{ background: 'rgb(var(--aethex-bg))', color: 'rgb(var(--aethex-text))', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 5, padding: '4px 6px', fontSize: 11, cursor: 'pointer', outline: 'none' }}
                    >
                      {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>

                    {!isBanned && (
                      <>
                        <button
                          onClick={() => setConfirm({ memberId: member.id, action: 'timeout' })}
                          title="Timeout member"
                          style={{ width: 28, height: 28, borderRadius: 5, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', transition: 'background 0.15s, color 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(6,182,212,0.2)'; e.currentTarget.style.color = '#06b6d4'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
                        >⏱️</button>
                        <button
                          onClick={() => setConfirm({ memberId: member.id, action: 'kick' })}
                          title="Kick member"
                          style={{ width: 28, height: 28, borderRadius: 5, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', transition: 'background 0.15s, color 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(245,158,11,0.2)'; e.currentTarget.style.color = '#f59e0b'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
                        >👢</button>
                        <button
                          onClick={() => setConfirm({ memberId: member.id, action: 'ban' })}
                          title="Ban member"
                          style={{ width: 28, height: 28, borderRadius: 5, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', transition: 'background 0.15s, color 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; e.currentTarget.style.color = '#ef4444'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; }}
                        >🔨</button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Confirmation dialog */}
      {confirm && confirmMeta && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
          onClick={() => setConfirm(null)}
        >
          <div style={{ background: 'rgb(var(--aethex-panel))', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 24, width: 360, boxShadow: '0 16px 48px rgba(0,0,0,0.6)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8, color: confirm.action === 'ban' ? '#ef4444' : confirm.action === 'timeout' ? '#06b6d4' : '#f59e0b' }}>
              {confirm.action === 'ban' ? '🔨 Ban Member' : confirm.action === 'timeout' ? '⏱️ Timeout Member' : '👢 Kick Member'}
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: confirm.action === 'timeout' ? 12 : 20, lineHeight: 1.5 }}>
              {confirm.action === 'timeout'
                ? <>Temporarily restrict <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{confirmMeta.name || confirmMeta.username}</strong> from sending messages.</>
                : <>Are you sure you want to {confirm.action} <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{confirmMeta.name || confirmMeta.username}</strong>?{confirm.action === 'ban' && ' They will not be able to rejoin this server.'}</>
              }
            </div>
            {confirm.action === 'timeout' && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Duration</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {TIMEOUT_DURATIONS.map(d => (
                    <button key={d.label} onClick={() => setTimeoutDuration(d)}
                      style={{ padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.12s',
                        background: timeoutDuration.ms === d.ms ? 'rgba(6,182,212,0.2)' : 'rgba(255,255,255,0.05)',
                        border: `1px solid ${timeoutDuration.ms === d.ms ? 'rgba(6,182,212,0.5)' : 'rgba(255,255,255,0.09)'}`,
                        color: timeoutDuration.ms === d.ms ? '#06b6d4' : 'rgba(255,255,255,0.5)',
                      }}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirm(null)} style={{ padding: '8px 16px', borderRadius: 6, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
              <button
                onClick={executeAction}
                disabled={actionLoading}
                style={{ padding: '8px 16px', borderRadius: 6, background: confirm.action === 'ban' ? '#dc2626' : confirm.action === 'timeout' ? '#0891b2' : '#d97706', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13, opacity: actionLoading ? 0.6 : 1 }}
              >
                {actionLoading ? 'Working…' : confirm.action === 'ban' ? 'Ban' : confirm.action === 'timeout' ? `Timeout (${timeoutDuration.label})` : 'Kick'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
